// Housekeeping · calidad (949): inspección con defectos y puntaje, llaves
// maestras, cierre de turno y ajustes (checklist, defectos, parámetros).
// Lo usa Housekeeping.tsx (tablero de la ama de llaves). Escrituras nivel ≥3
// (lo valida el servidor).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, KeyRound, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Panel, fieldCls, fmtFecha, hotelRpc, QK, type HkHabitacion, type CamareraHk } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Defecto { id: string; area: string; texto: string; peso: number; activo: boolean; orden: number }
interface Ajustes {
  creditos_max: number; min_salida: number; min_ocupada: number; min_libre: number; umbral: number; muestreo: number;
  bono_activo?: boolean; bono_meta?: number; bono_valor?: number; bono_aprobacion?: number;
  checklist: { id: string; tipo: 'salida' | 'ocupada' | 'libre'; texto: string; icono: string; orden: number; activo: boolean }[];
  defectos: Defecto[];
}
interface InspeccionFila { id: string; fecha: string; habitacion: string; camarera: string | null; puntaje: number; resultado: string; defectos: string[] | null; nota: string | null; observacion: string | null; inspector: string | null }
interface Llave { id: string; codigo: string; descripcion: string | null; activo: boolean; movimiento_id: string | null; camarera_id: string | null; camarera: string | null; entregada_at: string | null; entregada_por: string | null }
interface Resumen {
  fecha: string; tareas: number; terminadas: number; pendientes: { numero: string; estado: string; camarera: string | null }[];
  sucias_sin_camarera: string[]; no_molestar: string[]; por_inspeccionar: number; inspecciones: number; rechazadas: number;
  discrepancias: number; averias: number; llaves_sin_devolver: { codigo: string; camarera: string }[];
  camareras: { nombre: string; tareas: number; terminadas: number; creditos: number; min_promedio: number | null }[];
}
interface Cierre { id: string; fecha: string; created_at: string; por: string | null; novedades: string | null; resumen: Resumen }

const hora = (iso: string) => new Date(iso).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });

// ── Panel de inspección de una habitación terminada ────────────────────────
export function PanelInspeccion({ hab, onClose, onDone }: { hab: HkHabitacion; onClose: () => void; onDone: () => void }) {
  const { data: aj } = useQuery<Ajustes>({ queryKey: [QK, 'hk-ajustes'], queryFn: () => hotelRpc('hk_ajustes', {}) });
  const [sel, setSel] = useState<string[]>([]);
  const [nota, setNota] = useState('');
  const defectos = (aj?.defectos ?? []).filter((d) => d.activo);
  const porArea = useMemo(() => {
    const m = new Map<string, Defecto[]>();
    defectos.forEach((d) => m.set(d.area, [...(m.get(d.area) ?? []), d]));
    return [...m.entries()];
  }, [defectos]);
  const puntaje = Math.max(0, 100 - defectos.filter((d) => sel.includes(d.id)).reduce((a, d) => a + d.peso, 0));
  const umbral = aj?.umbral ?? 85;
  const aprueba = puntaje >= umbral;
  const guardar = useMutation({
    mutationFn: () => hotelRpc<{ resultado: string; puntaje: number }>('hk_inspeccionar', { _tarea: hab.tarea_id, _defectos: sel, _nota: nota.trim() || null }),
    onSuccess: (r) => {
      toast[r.resultado === 'aprobada' ? 'success' : 'warning'](r.resultado === 'aprobada'
        ? `Habitación ${hab.numero} inspeccionada (${r.puntaje} puntos)`
        : `Rechazada (${r.puntaje} puntos): vuelve a ${hab.camarera ?? 'la camarera'} como re-limpieza prioritaria`);
      onDone();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Panel title={`Inspeccionar ${hab.numero}${hab.camarera ? ` · ${hab.camarera}` : ''}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        {hab.inspeccion_motivo && <div className="text-xs text-muted-foreground">Inspección obligatoria: {hab.inspeccion_motivo}.</div>}
        {!!hab.faltantes && <div className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">La camarera marcó {hab.faltantes} paso(s) del checklist sin hacer.</div>}
        <p className="text-xs text-muted-foreground">Marque los defectos encontrados. Cada uno resta puntos; por debajo de {umbral} la habitación vuelve a la camarera para repasarla.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {porArea.map(([area, lista]) => (
            <div key={area} className="rounded-md border p-2">
              <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{area}</div>
              {lista.map((d) => (
                <label key={d.id} className="flex items-center gap-2 py-1">
                  <input type="checkbox" checked={sel.includes(d.id)} onChange={(e) => setSel(e.target.checked ? [...sel, d.id] : sel.filter((x) => x !== d.id))} />
                  <span className="flex-1">{d.texto}</span><span className="text-xs text-muted-foreground">−{d.peso}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
        <Campo label="Nota (opcional)"><input className={fieldCls} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Lo que la camarera debe saber" /></Campo>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <div className={`text-lg font-bold ${aprueba ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
            {puntaje} puntos · {aprueba ? 'Aprobada' : 'Re-limpieza'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
            <Button size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Registrar inspección</Button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">El puntaje evalúa la habitación. La camarera ve el resultado y puede dejar su observación; no reemplaza el procedimiento disciplinario.</p>
      </div>
    </Panel>
  );
}

// ── Pestaña Inspección: cola + historial ───────────────────────────────────
export function Inspeccion({ habs, onInspeccionar }: { habs: HkHabitacion[]; onInspeccionar: (h: HkHabitacion) => void }) {
  const { data: hist = [] } = useQuery<InspeccionFila[]>({ queryKey: [QK, 'hk-inspecciones'], queryFn: () => hotelRpc('hk_inspecciones_listar', { _desde: null }) });
  const cola = habs.filter((h) => h.inspeccion).sort((a, b) => (a.inspeccion === b.inspeccion ? 0 : a.inspeccion === 'obligatoria' ? -1 : 1) || (b.prioridad ?? 0) - (a.prioridad ?? 0));
  const aprob = hist.length ? Math.round((hist.filter((x) => x.resultado === 'aprobada').length / hist.length) * 100) : null;
  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Por inspeccionar ({cola.length})</h2>
        <p className="text-xs text-muted-foreground">Obligatorias: llegadas de hoy, VIP, re-limpiezas y terminadas muy rápido. Del resto, una muestra al azar (Ajustes).</p>
        {cola.length === 0 && <div className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">Nada por inspeccionar ✓</div>}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {cola.map((h) => (
            <div key={h.id} className={`flex items-center justify-between gap-2 rounded-lg border p-2 ${h.inspeccion === 'obligatoria' ? 'border-amber-400' : ''}`}>
              <div>
                <div className="font-semibold">{h.numero} <span className="text-xs font-normal text-muted-foreground">· {h.camarera ?? '—'} · {h.minutos ?? '—'} min</span></div>
                <div className="text-xs text-muted-foreground">{h.inspeccion === 'obligatoria' ? `Obligatoria: ${h.inspeccion_motivo}` : 'Muestra al azar'}{h.faltantes ? ` · faltó ${h.faltantes} paso(s)` : ''}</div>
              </div>
              <Button size="sm" onClick={() => onInspeccionar(h)}><ClipboardCheck className="mr-1 h-4 w-4" /> Inspeccionar</Button>
            </div>
          ))}
        </div>
      </section>
      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Últimos 7 días{aprob != null ? ` · ${aprob} % aprobadas` : ''}</h2>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Fecha</th><th className="py-2 pr-2">Hab.</th><th className="py-2 pr-2">Camarera</th>
              <th className="py-2 pr-2 text-right">Puntos</th><th className="py-2 pr-2">Resultado</th><th className="py-2 pr-2">Defectos</th><th className="py-2 pr-2">Observación de la camarera</th>
            </tr></thead>
            <tbody>
              {hist.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin inspecciones.</td></tr>}
              {hist.map((x, i) => (
                <tr key={x.id} className="border-b last:border-0">
                  <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-2 whitespace-nowrap">{fmtFecha(x.fecha)} {hora(x.fecha)}</td>
                  <td className="py-1.5 pr-2 font-mono">{x.habitacion}</td>
                  <td className="py-1.5 pr-2">{x.camarera ?? '—'}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{x.puntaje}</td>
                  <td className={`py-1.5 pr-2 ${x.resultado === 'rechazada' ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{x.resultado === 'rechazada' ? 'Re-limpieza' : 'Aprobada'}</td>
                  <td className="py-1.5 pr-2 text-xs">{x.defectos?.join(' · ') ?? '—'}{x.nota ? <div className="text-muted-foreground">Nota: {x.nota}</div> : null}</td>
                  <td className="py-1.5 pr-2 text-xs">{x.observacion ?? <span className="text-muted-foreground">—</span>}</td>
                </tr>
              ))}
            </tbody>
            {hist.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
              <td colSpan={4} className="py-2 pl-3">Total: {hist.length}</td>
              <td className="py-2 pr-2 text-right tabular-nums">{Math.round(hist.reduce((a, x) => a + x.puntaje, 0) / hist.length)} prom.</td>
              <td colSpan={3} className="py-2 pr-2">{hist.filter((x) => x.resultado === 'rechazada').length} re-limpiezas</td>
            </tr></tfoot>}
          </table>
        </div>
      </section>
    </div>
  );
}

// ── Pestaña Llaves maestras ────────────────────────────────────────────────
export function Llaves({ camareras, puedeEditar }: { camareras: CamareraHk[]; puedeEditar: boolean }) {
  const qc = useQueryClient();
  const { data: llaves = [] } = useQuery<Llave[]>({ queryKey: [QK, 'hk-llaves'], queryFn: () => hotelRpc('hk_llaves_listar', {}), refetchInterval: 30000 });
  const [nueva, setNueva] = useState<{ codigo: string; descripcion: string } | null>(null);
  const [entregarA, setEntregarA] = useState<Record<string, string>>({});
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });
  const guardar = useMutation({
    mutationFn: () => hotelRpc('hk_llave_guardar', { _d: nueva }),
    onSuccess: () => { toast.success('Llave registrada'); setNueva(null); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const entregar = useMutation({
    mutationFn: (x: { llave: string; camarera: string }) => hotelRpc('hk_llave_entregar', { _llave: x.llave, _camarera: x.camarera, _nota: null }),
    onSuccess: () => { toast.success('Llave entregada'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const recibir = useMutation({
    mutationFn: (llave: string) => hotelRpc('hk_llave_recibir', { _llave: llave, _nota: null }),
    onSuccess: () => { toast.success('Llave recibida'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const afuera = llaves.filter((l) => l.movimiento_id).length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Cada entrega y devolución queda con la hora y el usuario que la registró. Al cerrar el turno no deben quedar llaves afuera.</p>
        {puedeEditar && <Button size="sm" onClick={() => setNueva({ codigo: '', descripcion: '' })}><Plus className="mr-1 h-4 w-4" /> Nueva llave</Button>}
      </div>
      {nueva && (
        <Panel title="Nueva llave maestra" onClose={() => setNueva(null)}>
          <div className="grid gap-3 text-sm sm:grid-cols-3">
            <Campo label="Código *"><input className={fieldCls} value={nueva.codigo} onChange={(e) => setNueva({ ...nueva, codigo: e.target.value })} placeholder="M1" /></Campo>
            <Campo label="Descripción"><input className={fieldCls} value={nueva.descripcion} onChange={(e) => setNueva({ ...nueva, descripcion: e.target.value })} placeholder="Maestra piso 1" /></Campo>
            <div className="flex items-end justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setNueva(null)}>Cancelar</Button>
              <Button size="sm" disabled={!nueva.codigo.trim()} isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Panel>
      )}
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Llave</th><th className="py-2 pr-2">La tiene</th><th className="py-2 pr-2">Desde</th><th className="py-2 pr-2"></th>
          </tr></thead>
          <tbody>
            {llaves.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin llaves registradas.</td></tr>}
            {llaves.map((l, i) => (
              <tr key={l.id} className="border-b last:border-0">
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-2"><KeyRound className="mr-1 inline h-3.5 w-3.5" /><span className="font-mono font-semibold">{l.codigo}</span> <span className="text-muted-foreground">{l.descripcion}</span></td>
                <td className="py-1.5 pr-2">{l.camarera ?? <span className="text-muted-foreground">En la oficina</span>}</td>
                <td className="py-1.5 pr-2 text-xs">{l.entregada_at ? `${fmtFecha(l.entregada_at)} ${hora(l.entregada_at)} · ${l.entregada_por ?? ''}` : '—'}</td>
                <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                  {puedeEditar && (l.movimiento_id
                    ? <Button size="sm" variant="outline" className="h-7 px-2 text-xs" isLoading={recibir.isPending} onClick={() => recibir.mutate(l.id)}>Recibir</Button>
                    : <span className="inline-flex gap-1">
                        <select className="h-7 rounded border bg-background px-1 text-xs" value={entregarA[l.id] ?? ''} onChange={(e) => setEntregarA({ ...entregarA, [l.id]: e.target.value })}>
                          <option value="">— Camarera —</option>
                          {camareras.filter((c) => c.activo).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!entregarA[l.id]} onClick={() => entregar.mutate({ llave: l.id, camarera: entregarA[l.id] })}>Entregar</Button>
                      </span>)}
                </td>
              </tr>
            ))}
          </tbody>
          {llaves.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={5} className="py-2 pl-3">Total: {llaves.length} · afuera: {afuera}</td></tr></tfoot>}
        </table>
      </div>
    </div>
  );
}

// ── Cierre de turno ────────────────────────────────────────────────────────
export function CierreTurno({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data: r } = useQuery<Resumen>({ queryKey: [QK, 'hk-cierre'], queryFn: () => hotelRpc('hk_cierre_resumen', {}), staleTime: 0 });
  const { data: hist = [] } = useQuery<Cierre[]>({ queryKey: [QK, 'hk-cierres'], queryFn: () => hotelRpc('hk_cierres_listar', {}) });
  const [nov, setNov] = useState('');
  const guardar = useMutation({
    mutationFn: () => hotelRpc('hk_cierre_guardar', { _novedades: nov.trim() || null }),
    onSuccess: () => { toast.success('Cierre de turno registrado'); void qc.invalidateQueries({ queryKey: [QK] }); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const linea = (l: string, v: React.ReactNode, alerta = false) => (
    <div className={`flex justify-between gap-2 border-b py-1 last:border-0 ${alerta ? 'font-semibold text-red-700 dark:text-red-400' : ''}`}><span>{l}</span><span className="text-right">{v}</span></div>
  );
  return (
    <Panel title="Cierre de turno" onClose={onClose}>
      {!r ? <div className="py-4 text-sm text-muted-foreground">Cargando…</div> : (
        <div className="grid gap-4 text-sm lg:grid-cols-2">
          <div>
            {linea('Habitaciones del día', `${r.terminadas} terminadas de ${r.tareas}`)}
            {linea('Quedan pendientes', r.pendientes.length ? r.pendientes.map((p) => `${p.numero}${p.camarera ? ` (${p.camarera})` : ''}`).join(', ') : 'ninguna', r.pendientes.length > 0)}
            {linea('Sucias sin camarera', r.sucias_sin_camarera.length ? r.sucias_sin_camarera.join(', ') : 'ninguna', r.sucias_sin_camarera.length > 0)}
            {linea('No molestar sin resolver', r.no_molestar.length ? r.no_molestar.join(', ') : 'ninguna')}
            {linea('Inspecciones', `${r.inspecciones} · ${r.rechazadas} re-limpiezas · ${r.por_inspeccionar} obligatorias pendientes`, r.por_inspeccionar > 0)}
            {linea('Discrepancias abiertas', r.discrepancias, r.discrepancias > 0)}
            {linea('Averías reportadas hoy', r.averias)}
            {linea('Llaves sin devolver', r.llaves_sin_devolver.length ? r.llaves_sin_devolver.map((l) => `${l.codigo} (${l.camarera})`).join(', ') : 'ninguna', r.llaves_sin_devolver.length > 0)}
          </div>
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs">
                <thead><tr className="border-b text-left text-muted-foreground"><th className="py-1 pl-2">Camarera</th><th className="py-1 text-right">Hechas</th><th className="py-1 text-right">Créditos</th><th className="py-1 pr-2 text-right">Min. prom.</th></tr></thead>
                <tbody>
                  {r.camareras.map((c) => (
                    <tr key={c.nombre} className="border-b last:border-0"><td className="py-1 pl-2">{c.nombre}</td><td className="py-1 text-right tabular-nums">{c.terminadas}/{c.tareas}</td>
                      <td className="py-1 text-right tabular-nums">{c.creditos}</td><td className="py-1 pr-2 text-right tabular-nums">{c.min_promedio ?? '—'}</td></tr>
                  ))}
                  {r.camareras.length === 0 && <tr><td colSpan={4} className="py-3 text-center text-muted-foreground">Sin asignaciones hoy</td></tr>}
                </tbody>
              </table>
            </div>
            <Campo label={r.llaves_sin_devolver.length ? 'Novedades (obligatorio: hay llaves afuera)' : 'Novedades del turno'}>
              <textarea className={`${fieldCls} h-20`} value={nov} onChange={(e) => setNov(e.target.value)} placeholder="Lo que el siguiente turno y recepción deben saber" />
            </Campo>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
              <Button size="sm" isLoading={guardar.isPending} disabled={r.llaves_sin_devolver.length > 0 && !nov.trim()} onClick={() => guardar.mutate()}>Firmar cierre</Button>
            </div>
          </div>
          {hist.length > 0 && (
            <div className="lg:col-span-2">
              <div className="mb-1 text-xs font-semibold text-muted-foreground">Cierres anteriores</div>
              <div className="max-h-40 space-y-1 overflow-y-auto text-xs">
                {hist.map((c) => (
                  <div key={c.id} className="rounded border px-2 py-1">
                    <span className="font-medium">{fmtFecha(c.created_at)} {hora(c.created_at)}</span> · {c.por ?? '—'} · {c.resumen.terminadas}/{c.resumen.tareas} terminadas
                    {c.novedades && <div className="text-muted-foreground">{c.novedades}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

// ── Pestaña Ajustes ────────────────────────────────────────────────────────
export function AjustesHk({ puedeEditar }: { puedeEditar: boolean }) {
  const qc = useQueryClient();
  const { data: aj } = useQuery<Ajustes>({ queryKey: [QK, 'hk-ajustes'], queryFn: () => hotelRpc('hk_ajustes', {}) });
  const [par, setPar] = useState<Partial<Ajustes> | null>(null);
  const [nuevoPaso, setNuevoPaso] = useState<Record<string, string>>({});
  const [nuevoDef, setNuevoDef] = useState({ area: '', texto: '', peso: 10 });
  const guardar = useMutation({
    mutationFn: (d: Record<string, unknown>) => hotelRpc('hk_ajustes_guardar', { _d: d }),
    onSuccess: () => { toast.success('Guardado'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (!aj) return <div className="py-6 text-sm text-muted-foreground">Cargando…</div>;
  const p = { ...aj, ...par };
  const num = (k: keyof Ajustes, l: string) => (
    <Campo label={l}><input type="number" step="any" className={fieldCls} disabled={!puedeEditar} value={String(p[k] ?? '')} onChange={(e) => setPar({ ...par, [k]: Number(e.target.value) })} /></Campo>
  );
  return (
    <div className="space-y-5 text-sm">
      <section className="space-y-2">
        <h2 className="font-semibold">Parámetros</h2>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {num('creditos_max', 'Tope de créditos por turno')}
          {num('min_salida', 'Minutos estándar: salida')}
          {num('min_ocupada', 'Minutos estándar: ocupada')}
          {num('min_libre', 'Minutos estándar: libre sucia')}
          {num('umbral', 'Puntaje mínimo para aprobar')}
          {num('muestreo', '% de muestra a inspeccionar')}
        </div>
        <div className="grid gap-3 rounded-md border p-2 sm:grid-cols-4">
          <label className="flex items-center gap-2 text-sm sm:col-span-4">
            <input type="checkbox" disabled={!puedeEditar} checked={!!p.bono_activo} onChange={(e) => setPar({ ...par, bono_activo: e.target.checked })} />
            Bono por productividad y calidad (se envía a Nómina como ingreso; nunca descuenta)
          </label>
          {num('bono_meta', 'Créditos del mes que no generan bono')}
          {num('bono_valor', 'US$ por crédito sobre esa meta')}
          {num('bono_aprobacion', '% mínimo de inspecciones aprobadas')}
        </div>
        {puedeEditar && <Button size="sm" disabled={!par} isLoading={guardar.isPending} onClick={() => { guardar.mutate({ parametros: par }); setPar(null); }}>Guardar parámetros</Button>}
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold">Checklist por tipo de limpieza</h2>
        <div className="grid gap-3 lg:grid-cols-3">
          {(['salida', 'ocupada', 'libre'] as const).map((tipo) => (
            <div key={tipo} className="rounded-md border p-2">
              <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{tipo === 'salida' ? 'Salida' : tipo === 'ocupada' ? 'Ocupada (sigue)' : 'Libre sucia'}</div>
              {aj.checklist.filter((c) => c.tipo === tipo).map((c) => (
                <label key={c.id} className={`flex items-center gap-2 py-0.5 ${c.activo ? '' : 'text-muted-foreground line-through'}`}>
                  <input type="checkbox" checked={c.activo} disabled={!puedeEditar}
                    onChange={(e) => guardar.mutate({ checklist: { id: c.id, texto: c.texto, activo: e.target.checked } })} />
                  {c.texto}
                </label>
              ))}
              {puedeEditar && (
                <div className="mt-1 flex gap-1">
                  <input className={`${fieldCls} h-8`} placeholder="Nuevo paso" value={nuevoPaso[tipo] ?? ''} onChange={(e) => setNuevoPaso({ ...nuevoPaso, [tipo]: e.target.value })} />
                  <Button size="sm" variant="outline" disabled={!nuevoPaso[tipo]?.trim()}
                    onClick={() => { guardar.mutate({ checklist: { tipo, texto: nuevoPaso[tipo] } }); setNuevoPaso({ ...nuevoPaso, [tipo]: '' }); }}><Plus className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold">Defectos de inspección</h2>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Área</th><th className="py-2 pr-2">Defecto</th><th className="py-2 pr-2 text-right">Resta</th><th className="py-2 pr-2">Activo</th></tr></thead>
            <tbody>
              {aj.defectos.map((d, i) => (
                <tr key={d.id} className={`border-b last:border-0 ${d.activo ? '' : 'text-muted-foreground'}`}>
                  <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td><td className="py-1.5 pr-2">{d.area}</td><td className="py-1.5 pr-2">{d.texto}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{d.peso}</td>
                  <td className="py-1.5 pr-2"><input type="checkbox" checked={d.activo} disabled={!puedeEditar} onChange={(e) => guardar.mutate({ defecto: { id: d.id, texto: d.texto, activo: e.target.checked } })} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={5} className="py-2 pl-3">Total: {aj.defectos.length} ({aj.defectos.filter((d) => d.activo).length} activos)</td></tr></tfoot>
          </table>
        </div>
        {puedeEditar && (
          <div className="flex flex-wrap items-end gap-2">
            <Campo label="Área"><input className={`${fieldCls} w-36`} value={nuevoDef.area} onChange={(e) => setNuevoDef({ ...nuevoDef, area: e.target.value })} placeholder="Baño" /></Campo>
            <Campo label="Defecto"><input className={`${fieldCls} w-64`} value={nuevoDef.texto} onChange={(e) => setNuevoDef({ ...nuevoDef, texto: e.target.value })} /></Campo>
            <Campo label="Resta"><input type="number" min={1} max={100} className={`${fieldCls} w-20`} value={nuevoDef.peso} onChange={(e) => setNuevoDef({ ...nuevoDef, peso: Number(e.target.value) })} /></Campo>
            <Button size="sm" disabled={!nuevoDef.texto.trim()} onClick={() => { guardar.mutate({ defecto: nuevoDef }); setNuevoDef({ area: '', texto: '', peso: 10 }); }}>
              <Plus className="mr-1 h-4 w-4" /> Agregar defecto
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
